import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, CheckCircle, Clock, Target, Rocket, ArrowRight } from 'lucide-react';
import { ProductPricing } from '@/services/discount.service';
import { formatPrice } from '@/hooks/useFeaturedPricing';

/**
 * Props for AIToolsAcceleratorCard component
 */
interface AIToolsAcceleratorCardProps {
  /** Pricing data from API - if not provided, shows loading state */
  pricing?: ProductPricing | null;
  /** Whether pricing is currently loading */
  loading?: boolean;
}

/**
 * AIToolsAcceleratorCard Component
 *
 * A reusable card component for displaying the AI Tools Accelerator course
 * with professional styling, hover effects, and call-to-action buttons.
 *
 * Pricing is dynamically fetched from the backend pricing API.
 *
 * @component
 * @example
 * ```tsx
 * const { aiToolsPricing, loading } = useFeaturedPricing();
 * <AIToolsAcceleratorCard pricing={aiToolsPricing} loading={loading} />
 * ```
 */
export default function AIToolsAcceleratorCard({ pricing, loading = false }: AIToolsAcceleratorCardProps) {
  // Extract pricing values with fallbacks for loading state
  const displayPrice = pricing?.display_price ?? 0;
  const originalPrice = pricing?.original_price ?? 0;
  const isSaleActive = pricing?.is_sale_active ?? false;
  const savingsPercentage = pricing?.savings_percentage ?? 0;

  return (
    <div className="group relative">
      {/* Glow Effect */}
      <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-3xl blur-2xl opacity-20 group-hover:opacity-40 transition-all duration-500"></div>

      {/* Card */}
      <div className="relative bg-gradient-to-br from-white/[0.12] to-white/[0.05] backdrop-blur-xl rounded-3xl border border-white/20 group-hover:border-blue-500/50 transition-all duration-500 overflow-hidden h-full">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

        <div className="relative p-8 h-full flex flex-col">
          {/* Icon & Badge */}
          <div className="flex items-start justify-between mb-6">
            <div className="relative">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-2xl shadow-blue-500/50">
                <Zap className="w-8 h-8" strokeWidth={2.5} />
              </div>
              <div className="absolute -inset-2 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-500"></div>
            </div>
            <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-blue-500/30 px-4 py-1.5 rounded-full">
              <span className="text-sm font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                TRENDING
              </span>
            </div>
          </div>

          {/* Title */}
          <h3 className="text-3xl font-bold mb-4 leading-tight group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-cyan-400 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
            AI Tools Accelerator
          </h3>

          {/* Subtitle */}
          <p className="text-lg text-blue-300/90 mb-6 font-medium">
            Master the Essential Tools for Work, Business & Automation
          </p>

          {/* Description */}
          <p className="text-gray-400 mb-8 leading-relaxed flex-grow">
            From ChatGPT to Midjourney, HeyGen to Claude Code - master 20+ cutting-edge AI tools that are reshaping every industry. Build complete AI-powered workflows in days, not months.
          </p>

          {/* Key Benefits */}
          <div className="space-y-3 mb-8">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
              <span className="text-sm text-gray-300">Master 20+ AI tools across 8 essential categories</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
              <span className="text-sm text-gray-300">Build complete automation workflows end-to-end</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
              <span className="text-sm text-gray-300">Get tool templates & integration guides worth $2000+</span>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 mb-6 flex-wrap">
            <div className="flex items-center space-x-2 bg-white/5 px-3 py-2 rounded-lg">
              <Clock className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-gray-300">8 hours</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/5 px-3 py-2 rounded-lg">
              <Target className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-gray-300">Beginner Friendly</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/5 px-3 py-2 rounded-lg">
              <Rocket className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-gray-300">Cutting Edge</span>
            </div>
          </div>

          {/* Pricing & CTA */}
          <div className="border-t border-white/10 pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                {loading ? (
                  /* Loading skeleton */
                  <div className="space-y-2">
                    <div className="h-10 w-24 bg-white/10 rounded animate-pulse"></div>
                    <div className="h-4 w-32 bg-white/10 rounded animate-pulse"></div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-baseline gap-3">
                      <span className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                        {formatPrice(displayPrice)}
                      </span>
                      {isSaleActive && originalPrice > displayPrice && (
                        <span className="text-2xl text-gray-300 line-through font-medium decoration-2">
                          {formatPrice(originalPrice)}
                        </span>
                      )}
                    </div>
                    {isSaleActive && savingsPercentage > 0 && (
                      <p className="text-sm text-blue-400 mt-1">
                        Save {savingsPercentage}% - Limited Time
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
            <Link
              to="/ai-tools-accelerator"
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 py-4 rounded-xl font-bold text-lg flex items-center justify-center space-x-2 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/50 group/btn"
            >
              <span>Start Learning Now</span>
              <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
